"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { DatePicker } from "@/components/ui/date-picker"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useLeadForm } from "@/hooks/use-lead-form"
import { INVENTORY } from "@/lib/inventory"
import { motion } from "framer-motion"
import { AlertCircle, CheckCircle, MessageCircle, Plus, X } from "lucide-react"
import { Controller, useFieldArray } from "react-hook-form"
import { CONTACT } from '@/lib/constants'
import { cityData } from "@/lib/cities-data"
import { useState } from "react"
import { serviceTypes } from "@/lib/form-options"

interface LeadFormProps {
  inModal?: boolean
}

export function LeadForm({ inModal = false }: LeadFormProps) {
  const { form, isSubmitted, submitError, onSubmit } = useLeadForm()
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "inventory",
  })

  const [newItem, setNewItem] = useState({
    category: "",
    itemId: "",
    typeId: "",
    quantity: 1,
  })

  // Get unique categories
  const categories = Array.from(new Set(INVENTORY.map(item => item.category)))

  // Get items for selected category
  const availableItems = newItem.category
    ? INVENTORY.find(cat => cat.category === newItem.category)?.items || []
    : []

  // Get types for selected item
  const availableTypes = newItem.itemId
    ? availableItems.find(item => item.id === newItem.itemId)?.types || []
    : []

  const handleAddItem = () => {
    if (newItem.category && newItem.itemId && newItem.typeId) {
      const selectedItem = availableItems.find(item => item.id === newItem.itemId)
      const selectedType = availableTypes.find(type => type.id === newItem.typeId)
      
      if (selectedItem && selectedType) {
        append({
          category: newItem.category,
          itemId: newItem.itemId,
          itemName: selectedItem.name,
          typeId: newItem.typeId,
          typeName: selectedType.label,
          quantity: newItem.quantity,
        })
        setNewItem({ category: "", itemId: "", typeId: "", quantity: 1 })
      }
    }
  }

  const container = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.1 } },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  }

  const FormContent = (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 relative z-10">
      {/* Personal Info */}
      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            placeholder="John Doe"
            {...form.register("name")}
            className="h-11"
          />
          {form.formState.errors.name && (
            <p className="text-xs text-red-600 mt-1">{form.formState.errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            {...form.register("email")}
            className="h-11"
          />
          {form.formState.errors.email && (
            <p className="text-xs text-red-600 mt-1">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            type="tel"
            placeholder={CONTACT.phone.display}
            {...form.register("phone")}
            className="h-11"
          />
          {form.formState.errors.phone && (
            <p className="text-xs text-red-600 mt-1">{form.formState.errors.phone.message}</p>
          )}
        </div>
      </div>

      {/* Move Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
        <Controller
          name="from"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Moving From *</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-11" aria-label="Select origin city">
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {cityData.map((city) => (
                    <SelectItem key={city.id} value={city.name}>
                      {city.name}, {city.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.from && (
                <p className="text-xs text-red-600 mt-1">{form.formState.errors.from.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="to"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Moving To *</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-11" aria-label="Select destination city">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {cityData.map((city) => (
                    <SelectItem key={city.id} value={city.name}>
                      {city.name}, {city.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.to && (
                <p className="text-xs text-red-600 mt-1">{form.formState.errors.to.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="moveType"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Move Type *</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-11" aria-label="Select move type">
                  <SelectValue placeholder="Select move type" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.moveType && (
                <p className="text-xs text-red-600 mt-1">{form.formState.errors.moveType.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="moveDate"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>Move Date</Label>
              <DatePicker
                date={field.value}
                onDateChange={field.onChange}
                placeholder="Select date"
                className="h-11"
              />
            </div>
          )}
        />
      </div>

      {/* Inventory Section */}
      <div className="space-y-4 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Inventory (Optional)</Label>
          <span className="text-xs text-muted-foreground">{fields.length} items added</span>
        </div>

        {/* Add Item Form */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="space-y-2">
            <Label className="text-xs">Category</Label>
            <Select
              value={newItem.category}
              onValueChange={(value) => setNewItem({ ...newItem, category: value, itemId: "", typeId: "" })}
            >
              <SelectTrigger className="h-10" aria-label="Select inventory category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Item</Label>
            <Select
              value={newItem.itemId}
              onValueChange={(value) => setNewItem({ ...newItem, itemId: value, typeId: "" })}
              disabled={!newItem.category}
            >
              <SelectTrigger className="h-10" aria-label="Select inventory item">
                <SelectValue placeholder="Select item" />
              </SelectTrigger>
              <SelectContent>
                {availableItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Type</Label>
            <Select
              value={newItem.typeId}
              onValueChange={(value) => setNewItem({ ...newItem, typeId: value })}
              disabled={!newItem.itemId}
            >
              <SelectTrigger className="h-10" aria-label="Select item type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Quantity</Label>
            <Input
              type="number"
              min="1"
              max="100"
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs opacity-0">Add</Label>
            <Button
              type="button"
              onClick={handleAddItem}
              disabled={!newItem.category || !newItem.itemId || !newItem.typeId}
              className="w-full h-10 bg-[#003DA5] hover:bg-blue-700"
            >
              <Plus size={16} className="mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Added Items List */}
        {fields.length > 0 && (
          <div className="space-y-2">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-[#003DA5]/30 transition-colors"
              >
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{field.typeName}</p>
                  <p className="text-xs text-muted-foreground">
                    {field.category} • {field.itemName} • Qty: {field.quantity}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message">Additional Message</Label>
        <Textarea
          id="message"
          placeholder="Tell us about your move requirements..."
          {...form.register("message")}
          rows={3}
          className="resize-none"
        />
        {form.formState.errors.message && (
          <p className="text-xs text-red-600 mt-1">{form.formState.errors.message.message}</p>
        )}
      </div>

      {/* Status Messages */}
      {submitError && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
          <AlertCircle size={18} className="text-red-600" />
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>
      )}

      {isSubmitted && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
          <CheckCircle size={18} className="text-green-600" />
          <p className="text-green-700 text-sm">Success! Redirecting to WhatsApp...</p>
        </div>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={form.formState.isSubmitting || isSubmitted}
        className="w-full bg-gradient-to-r from-[#FF7A00] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-5 text-base shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
      >
        {form.formState.isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            Submitting...
          </>
        ) : (
          <>
            <MessageCircle size={20} />
            Get Quote
          </>
        )}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        By submitting, you agree to be contacted by Bigwin Relocation
      </p>
    </form>
  )

  if (inModal) {
    return FormContent
  }

  return (
    <section className="relative">
      <div className="relative max-w-5xl mx-auto">
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={item}>
            <Card className="p-6 sm:p-8 bg-white border-2 border-white/20 shadow-2xl rounded-3xl relative overflow-hidden backdrop-blur-sm">
              {FormContent}
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
